import { useState } from 'react'

import { api } from '../api/client'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * «Distribuzione di massa» nella finestra d'installazione di un agente: si
 * sceglie **dove consegnano** le macchine (il Control Plane o una sonda col
 * ricevitore acceso) e si scarica il pacchetto per Windows (zip) o Linux
 * (tar.gz), con dentro binario, configurazione e uno script che non scarica
 * niente. Una guida passo passo accanto: criterio di gruppo, Intune, SCCM,
 * Ansible, a mano.
 */
export function PacchettoDistribuzione({
  pacchetti,
  destinazioneAgente,
}: {
  pacchetti: Record<string, string>
  destinazioneAgente: string
}) {
  const destinazioni = useAsync(() => api.inventoryDeliveryTargets(), [])
  const [destinazione, setDestinazione] = useState(destinazioneAgente)
  const disponibili = (destinazioni.data ?? []).filter((d) => d.available)
  const link = (sistema: string) =>
    `${pacchetti[sistema]}?endpoint=${encodeURIComponent(destinazione)}`
  const scelta = disponibili.find((d) => d.value === destinazione)

  return (
    <div className="field">
      <label>{t('Distribuzione di massa')}</label>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Un pacchetto con l’agente e la sua configurazione che non scarica niente: su Windows install.cmd lancia l’agente, che si installa da solo senza script PowerShell (la strada che gli EDR non bloccano). Si mette su una share o in Intune/SCCM e si installa su quante macchine vuoi, ognuna diventa il suo CI. Contiene il segreto dell’agente: trattalo come una credenziale. Si scarica solo adesso (finestra d’installazione); una volta scaricato non scade, e lo invalidi rigenerando o revocando l’agente.')}
      </p>
      <div className="field">
        <label htmlFor="pacchetto-destinazione">{t('Le macchine consegnano a')}</label>
        <select id="pacchetto-destinazione" value={destinazione} onChange={(e) => setDestinazione(e.target.value)}>
          {disponibili.map((d) => (
            <option key={d.value || 'cloud'} value={d.value}>{d.label}</option>
          ))}
        </select>
        {scelta && <p className="field__hint">{scelta.hint}</p>}
      </div>
      <div className="rowactions" style={{ textAlign: 'left' }}>
        {pacchetti.windows && (
          <a className="pulsante-link" href={link('windows')} download>{t('Scarica pacchetto Windows (.zip)')}</a>
        )}{' '}
        {pacchetti['windows-msi'] && (
          <a className="pulsante-link secondary" href={link('windows-msi')} download>{t('Scarica MSI Windows (.msi)')}</a>
        )}{' '}
        {pacchetti['windows-gpo'] && (
          <a className="pulsante-link secondary" href={link('windows-gpo')} download>{t('Scarica pacchetto GPO (.zip)')}</a>
        )}{' '}
        {pacchetti.linux && (
          <a className="pulsante-link secondary" href={link('linux')} download>{t('Scarica pacchetto Linux (.tar.gz)')}</a>
        )}
      </div>
      <p className="field__hint" style={{ marginTop: 4 }}>
        {t('Per Windows aziendale: l’MSI compare in «App installate», si disinstalla pulito e si assegna con GPO «Installazione software», Intune o SCCM; il pacchetto GPO è lo zip con in più uno script di avvio del computer pronto da indicare nei Criteri di gruppo.')}
      </p>

      <details className="guida-chiave" style={{ marginTop: 8 }}>
        <summary>{t('Come si distribuisce il pacchetto?')}</summary>
        <div className="guida-chiave__corpo">
          <h4>{t('Prima di iniziare')}</h4>
          <ul>
            <li>{t('Scegli qui sopra dove consegnano le macchine: il Control Plane se lo raggiungono (HTTPS, porta 443), oppure una sonda della loro rete se il cloud non lo vedono (porta del ricevitore della sonda, di norma 8899). Se la sonda non risponde, l’agente riprova sul Control Plane.')}</li>
            <li>{t('Lo stesso pacchetto va bene per tutte le macchine: ognuna si presenta con il proprio seriale e nome e diventa un CI a sé.')}</li>
            <li>{t('Se l’agente è già installato il pacchetto non fa niente: puoi lanciarlo a ogni avvio senza doppioni. Per reinstallare (per esempio dopo aver rigenerato il segreto) usa /force su Windows, --force su Linux.')}</li>
          </ul>
          <h4>{t('Windows — criterio di gruppo')}</h4>
          <ol>
            <li>{t('Estrai lo zip in una cartella condivisa leggibile dai computer del dominio (per esempio \\\\dominio\\NETLOGON\\censo-inventory o una share con permesso di lettura per «Domain Computers»).')}</li>
            <li>{t('Gestione Criteri di gruppo → un GPO collegato alle OU dei server → Configurazione computer → Criteri → Impostazioni di Windows → Script → Avvio → Aggiungi: install.cmd dalla share.')}</li>
            <li>{t('Al riavvio successivo (o con gpupdate /force e un riavvio) ogni macchina installa l’agente come SYSTEM, registra l’attività «CENSO inventory» e fa la prima consegna.')}</li>
          </ol>
          <h4>{t('Windows — MSI (App installate, GPO «Installazione software»)')}</h4>
          <ol>
            <li>{t('Scarica l’MSI: è specifico di questo agente (porta dentro la configurazione e il segreto) e aggiorna in loco se reinstalli lo stesso agente.')}</li>
            <li>{t('A mano o in automazione: msiexec /i censo-inventory-*.msi /qn (installazione silenziosa, come SYSTEM). Compare in «App installate» come «CENSO · Agente d’inventario»; la disinstallazione toglie attività e file.')}</li>
            <li>{t('GPO: Configurazione computer → Criteri → Impostazioni software → Installazione software → Nuovo pacchetto → l’MSI dalla share, modalità «Assegnato». Si installa al riavvio.')}</li>
            <li>{t('Intune: carica l’MSI come app «Riga di comando» o LOB; SCCM: applicazione di tipo Windows Installer. Rilevamento: il prodotto MSI o C:\\ProgramData\\CENSO\\censo-inventory.exe.')}</li>
          </ol>
          <h4>{t('Windows — script di avvio GPO (pacchetto GPO)')}</h4>
          <ol>
            <li>{t('Estrai lo zip del pacchetto GPO su una share letta dai computer (es. \\\\dominio\\NETLOGON\\censo-inventory).')}</li>
            <li>{t('GPO → Configurazione computer → Criteri → Impostazioni di Windows → Script (avvio/arresto) → Avvio → Aggiungi: avvio-gpo.cmd dalla share.')}</li>
            <li>{t('Al riavvio installa solo dove manca (idempotente) e traccia in C:\\ProgramData\\CENSO\\gpo-avvio.log.')}</li>
          </ol>
          <h4>{t('Windows — Intune o SCCM')}</h4>
          <ol>
            <li>{t('Intune: impacchetta la cartella come app Win32 (IntuneWinAppUtil) con comando di installazione «install.cmd», contesto di sistema; rilevamento: il file C:\\ProgramData\\CENSO\\censo-inventory.exe.')}</li>
            <li>{t('SCCM / Configuration Manager: applicazione o pacchetto con programma «install.cmd», eseguito con diritti amministrativi, che l’utente sia connesso o no.')}</li>
          </ol>
          <h4>{t('Linux — Ansible o script')}</h4>
          <ol>
            <li>{t('Copia l’archivio sulle macchine ed estrailo: tar xzf censo-inventory-*-linux.tar.gz.')}</li>
            <li>{t('Da root: sh censo-inventory/install.sh. Sceglie da solo il binario amd64 o arm64, scrive la configurazione in /etc/censo e il cron in /etc/cron.d/censo-inventory.')}</li>
            <li>{t('Con Ansible: un task «unarchive» dell’archivio seguito da un task «command: sh /tmp/censo-inventory/install.sh» con become: true.')}</li>
            <li>{t('Oppure il playbook già pronto nel pacchetto: dalla cartella estratta, ansible-playbook -i <inventario> install.yml (copia il pacchetto sugli host e installa come root, idempotente; -e force=true per reinstallare).')}</li>
          </ol>
          <h4>{t('Verifica')}</h4>
          <p>{t('In questa pagina, su questo agente, «Consegne» sale e «Ultima consegna» mostra l’IP interno delle macchine; ogni macchina compare fra i Configuration Item con origine «Agente d’inventario».')}</p>
        </div>
      </details>
    </div>
  )
}
